/**
 * Relationship represents an edge in the knowledge graph.
 * Follows the RDF triple model: (subject, predicate, object)
 *
 * Renamed from ModelRelationship/Assertion as part of the TBox/ABox refactoring.
 * Implements the Edge interface from semantical-core.
 */

import type { Node, Edge } from "@semantical/modules/semantical-core";
import type { ObjectProperty } from "@semantical/modules/ontology-model";

// ArchiMate relationship types (kept for visual rendering, derived from ontology's sem:relationshipType)
export type RELATIONSHIP_TYPE =
    | "composition"      // solid line + filled black diamond at source
    | "aggregation"      // solid line + white (unfilled) diamond at source
    | "assignment"       // solid line + filled dot at target
    | "serving"          // solid line + open arrowhead at target
    | "influence"        // dashed line + open arrowhead, optional +/- suffix
    | "realization"      // dashed line + hollow triangular arrowhead
    | "flow"             // dashed line + open arrowhead
    | "triggering"       // solid line + open arrowhead
    | "specialization"   // solid line + hollow triangular arrowhead
    | "access"           // dotted line (read/write/readwrite variants)
    | "association"      // plain solid line
    | "junction_and"     // filled black circle
    | "junction_or";     // white circle

// Access mode for access relationships
export type ACCESS_MODE = "read" | "write" | "readwrite";

// Relationship rendering configuration
export interface RelationshipStyle {
    strokeStyle: "solid" | "dashed" | "dotted";
    strokeWidth: number;
    strokeColor: string;
    sourceMarker: string | null;
    targetMarker: string | null;
}

// Get the style configuration for each relationship type
export function getRelationshipStyle(type: RELATIONSHIP_TYPE, accessMode?: ACCESS_MODE): RelationshipStyle {
    const baseStyle: RelationshipStyle = {
        strokeStyle: "solid",
        strokeWidth: 1.5,
        strokeColor: "#000",
        sourceMarker: null,
        targetMarker: null
    };

    switch (type) {
        case "composition":
            return { ...baseStyle, sourceMarker: "compositionDiamond" };
        case "aggregation":
            return { ...baseStyle, sourceMarker: "aggregationDiamond" };
        case "assignment":
            return { ...baseStyle, targetMarker: "assignmentDot" };
        case "serving":
            return { ...baseStyle, targetMarker: "openArrowhead" };
        case "influence":
            return { ...baseStyle, strokeStyle: "dashed", targetMarker: "openArrowhead" };
        case "realization":
            return { ...baseStyle, strokeStyle: "dashed", targetMarker: "hollowTriangle" };
        case "flow":
            return { ...baseStyle, strokeStyle: "dashed", targetMarker: "openArrowhead" };
        case "triggering":
            return { ...baseStyle, targetMarker: "openArrowhead" };
        case "specialization":
            return { ...baseStyle, targetMarker: "hollowTriangle" };
        case "access":
            return {
                ...baseStyle,
                strokeStyle: "dotted",
                targetMarker: accessMode === "write" ? "openArrowhead" : null,
                sourceMarker: accessMode === "read" ? "accessBar" : null
            };
        case "association":
            return baseStyle;
        case "junction_and":
            return { ...baseStyle, sourceMarker: "junctionAnd" };
        case "junction_or":
            return { ...baseStyle, sourceMarker: "junctionOr" };
        default:
            return baseStyle;
    }
}

// Legacy type mapping for backwards compatibility
export const LEGACY_TYPE_MAP: Record<string, RELATIONSHIP_TYPE> = {
    "COMPOSITION": "composition",
    "AGGREGATION": "aggregation",
    "GENERALIZATION": "specialization"
};

/**
 * Generate a unique ID for an assertion
 */
function generateAssertionId(): string {
    return `rel:${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Relationship represents an edge in the knowledge graph.
 * Follows the RDF triple model: (subject, predicate, object)
 * - subject: the source Node
 * - predicate: the ObjectProperty from the domain ontology
 * - object: the target Node
 *
 * The visual properties (type, strokeStyle, markers) come from the predicate (ObjectProperty).
 */
export default class Relationship implements Edge {
    id: string;                    // Unique identifier for the relationship instance
    subject: Node;                 // Source element (subject of the triple)
    predicate: ObjectProperty;     // The ObjectProperty from the ontology (predicate of the triple)
    object: Node;                  // Target element (object of the triple)
    isInferred: boolean;           // Whether this relationship was inferred by the inference engine

    constructor(
        subject: Node,
        predicate: ObjectProperty,
        object: Node,
        id?: string,
        isInferred: boolean = false
    ) {
        this.id = id || generateAssertionId();
        this.subject = subject;
        this.predicate = predicate;
        this.object = object;
        this.isInferred = isInferred;
    }

    /**
     * Get the predicate URI (for Edge interface)
     */
    get predicateUri(): string {
        return this.predicate.uri;
    }

    /**
     * Get the display name (from the predicate label)
     */
    get name(): string {
        return this.predicate.label;
    }

    /**
     * Get the relationship type for visual rendering (from the predicate)
     */
    get type(): RELATIONSHIP_TYPE {
        return (this.predicate.relationshipType || 'association') as RELATIONSHIP_TYPE;
    }

    /**
     * Legacy accessor for source element
     */
    get sourceEnd() {
        return { modelClass: this.subject };
    }

    /**
     * Legacy accessor for target element
     */
    get targetEnd() {
        return { modelClass: this.object };
    }

    toJSON() {
        return {
            id: this.id,
            // Reference subject and object by their identifying URI
            // For SchemaClass: concept.uri, for Individual: owlIndividual.uri
            subjectRef: this.subject.uri,
            predicateRef: this.predicate.uri,  // Reference the ObjectProperty by URI
            objectRef: this.object.uri
        }
    }

    /**
     * Deserialize from JSON.
     * @param json The serialized relationship data
     * @param nodeResolver Function to resolve Node by concept URI
     * @param propertyResolver Function to resolve ObjectProperty by URI
     */
    static fromJSON(
        json: any,
        nodeResolver: (conceptUri: string) => Node | undefined,
        propertyResolver: (propertyUri: string) => ObjectProperty | undefined
    ): Relationship | null {
        const subject = nodeResolver(json.subjectRef);
        const object = nodeResolver(json.objectRef);
        const predicate = propertyResolver(json.predicateRef);

        if (!subject || !object) {
            console.warn(`Assertion.fromJSON: Could not resolve elements for relationship ${json.id}`);
            return null;
        }

        if (!predicate) {
            console.warn(`Assertion.fromJSON: Could not resolve predicate ${json.predicateRef} for relationship ${json.id}`);
            // Create a fallback predicate with minimal info
            // Extract local name from URI: check for # first, then /
            const uri = json.predicateRef;
            const hashIndex = uri.lastIndexOf('#');
            const slashIndex = uri.lastIndexOf('/');
            const label = hashIndex !== -1
                ? uri.substring(hashIndex + 1)
                : (slashIndex !== -1 ? uri.substring(slashIndex + 1) : 'unknown');
            const fallbackPredicate: ObjectProperty = {
                uri: uri,
                label: label
            };
            return new Relationship(subject, fallbackPredicate, object, json.id);
        }

        return new Relationship(subject, predicate, object, json.id);
    }
}
